// ┌──────────────────────────────────────────────┐
// │ Cart — Protection Plan Sync (E20-864)          │
// └──────────────────────────────────────────────┘
// Keeps each protection-plan line in lockstep with its parent machine, linked by
// the shared _bundle_id set at add-to-cart:
//   • machine qty changes → the plan's qty matches it
//   • machine removed     → the plan is auto-removed (no orphaned warranties)
//   • plan removed        → the machine stays, rebuilt as a clean line with its bundle
//                           properties gone (see rebuildStaleMachine)
// Removing the plan never removes the machine — we only ever follow the machine, and we
// never re-add a plan.
//
// Covers both cart surfaces: the drawer and the cart page both run through the
// theme's CartForm, which dispatches `cart:updated` on document after every rebuild.
//
// Clicking Remove is NOT handled here — bfx-cart-remove.js owns every Remove click,
// bundle cascade included, and shares its /cart.js and /cart/change.js helpers and its
// rebuild coordinator with this file via window.bfxCart. This file only reconciles
// quantities and stale properties.
//
// Why change.js (not update.js): a line item key is NOT stable — Shopify recomputes
// it whenever the line's properties or DISCOUNT allocations change, and a machine
// qty change recomputes discounts. `/cart/update.js` ADDS a new line when the key it
// gets no longer matches, which duplicated the machine/plan lines. `/cart/change.js`
// only ever changes an item already in the cart, so it can't duplicate. We match a
// plan to its machine by the stable `_bundle_id` property (never the volatile key),
// re-read /cart.js for the current key, and apply one change at a time — each change
// reshuffles the remaining keys, so stale keys can't be batched.

{
  // root/getCart/changeLine/requestBuild live in bfx-cart-remove.js (window.bfxCart) so the
  // two cart scripts can't drift apart. Dereferenced at call time rather than destructured
  // here, which would run before that script has defined it.
  const root = () => window.bfxCart.root();
  const getCart = () => window.bfxCart.getCart();
  const changeLine = (key, quantity) => window.bfxCart.changeLine(key, quantity);
  const requestBuild = () => window.bfxCart.requestBuild();

  // The reconcile in progress, if any, and how many calls are queued behind it — see sync().
  let inFlight = null;
  let waiting = 0;
  // Bundle ids already rebuilt this page load — see the guard in reconcileOnce().
  const rebuilt = new Set();
  // Variant ids already warned about as unresolvable, so the message logs once, not per pass.
  const warnedUnresolvable = new Set();

  const addLine = (variantId, quantity, properties) =>
    fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(
        properties && Object.keys(properties).length
          ? { id: variantId, quantity, properties }
          : { id: variantId, quantity }
      ),
    });

  // The first plan line whose qty doesn't match its machine, resolved against the
  // CURRENT cart, as { key, quantity }. Parent machine = has _bundle_id, no
  // _parent_sku; plan = has both. A missing machine (removed) yields quantity 0, so
  // the plan is removed.
  const nextFix = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return null;

    const machineQty = {};
    cart.items.forEach((item) => {
      const props = item.properties || {};
      if (props._bundle_id && !props._parent_sku) machineQty[props._bundle_id] = item.quantity;
    });

    const plan = cart.items.find((item) => {
      const props = item.properties || {};
      if (!props._bundle_id || !props._parent_sku) return false;
      return item.quantity !== (machineQty[props._bundle_id] || 0);
    });

    return plan ? { key: plan.key, quantity: machineQty[plan.properties._bundle_id] || 0 } : null;
  };

  // The first machine line still carrying bundle properties when its bundle has no plan
  // line left. Removing a plan can't clear the machine's properties server-side, so the
  // machine would otherwise reach the order still claiming a warranty that was never
  // bought — the target end state, per
  // docs/dev/architecture/protection-plan-order-data-contract.md § Relationship flags, is
  // BOTH properties absent on a plan-less machine. Best-effort, not guaranteed: the rebuild
  // below aborts rather than risk the line, so the cleanup can be deferred to a later load.
  // `skip` holds bundle ids already attempted this page load, so one bundle that can't be
  // finished doesn't hide the rest — see the caller.
  const nextStaleMachine = (cart, skip) => {
    if (!cart || !Array.isArray(cart.items)) return null;

    const bundlesWithPlan = new Set();
    cart.items.forEach((item) => {
      const props = item.properties || {};
      if (props._bundle_id && props._parent_sku) bundlesWithPlan.add(props._bundle_id);
    });

    // A machine carrying the flag with NO _bundle_id is a corrupt line we can't safely
    // rebuild: the removal pass re-resolves the original by bundle id, and a missing id
    // matches every property-less line of that variant — including the replacement we just
    // added, or an unrelated plain line the shopper chose. Warn instead of guessing.
    cart.items.forEach((item) => {
      const props = item.properties || {};
      if (props._has_assoc_protection_plan && !props._bundle_id && !props._parent_sku) {
        // Once per variant per page load — this runs on every reconcile pass, and the same
        // warning three times reads like three separate problems.
        if (warnedUnresolvable.has(item.variant_id)) return;
        warnedUnresolvable.add(item.variant_id);
        console.warn(
          `Protection plan cart sync: machine ${item.variant_id} carries _has_assoc_protection_plan with no _bundle_id — skipping, it can't be resolved to a bundle`
        );
      }
    });

    return (
      cart.items.find((item) => {
        const props = item.properties || {};
        if (props._parent_sku) return false; // that's a plan line, not a machine
        if (!props._bundle_id) return false; // clean already, or unresolvable (warned above)
        if (skip.has(props._bundle_id)) return false;
        return !bundlesWithPlan.has(props._bundle_id);
      }) || null
    );
  };

  // Rebuild a plan-less machine as a genuinely clean line.
  //
  // Why a rebuild and not an edit: /cart/change.js cannot remove a line's last remaining
  // property — an empty `properties` object is accepted and silently ignored, and writing
  // '' or null stores those literally. So the only way to get BOTH `_bundle_id` and
  // `_has_assoc_protection_plan` off the line is to replace the line.
  //
  // ⚠️ Add FIRST, then remove. The reverse order would delete a line we cannot guarantee
  // we can recreate — inventory can move between the two calls — and silently losing a
  // shopper's machine is far worse than the transient duplicate this order risks. There is
  // no batch endpoint, so two requests is the floor; see the doc for the residual risk.
  const rebuildStaleMachine = async (machine) => {
    const properties = { ...machine.properties };
    delete properties._bundle_id;
    delete properties._has_assoc_protection_plan;

    // Unrelated properties (engraving, gift note, an upload) ride across to the new line.
    const added = await addLine(machine.variant_id, machine.quantity, properties);

    // A failed add is a SAFE stop: the cart is untouched, so the stale line is left exactly as
    // it was. The likely cause is 422 — no inventory for a second unit while the original still
    // holds one. Note the caller has already marked this bundle attempted, so it won't be
    // retried until the next page load; retrying against unchanged inventory would just fail
    // again, and the guard is what keeps a rebuild from stacking duplicates.
    if (!added.ok) {
      console.error(
        `Protection plan cart sync: could not rebuild plan-less machine ${machine.variant_id} (add.js ${added.status}); leaving its stale bundle properties in place`
      );
      return { cart: null, mutated: false };
    }

    // Past this point the add has landed, so the cart HAS changed no matter what follows. Every
    // exit below reports mutated: true — the caller needs to re-render even on failure, or the
    // shopper is left looking at a cart that doesn't match the one they have.
    // Now drop the stale line. Its key shifts when the cart changes, so re-resolve it each
    // attempt by bundle id + machine role + variant rather than trusting the key we read.
    const bundleId = machine.properties._bundle_id;

    // The key the stale line had a moment ago usually still holds, so try it before paying for a
    // re-read (~200ms). It can't match the replacement, whose properties differ, so the worst case
    // is a rejection, and the loop below then resolves the line afresh as it always did.
    const direct = await changeLine(machine.key, 0).catch(() => null);
    if (direct?.ok) {
      const cart = (await direct.json().catch(() => null)) || (await getCart().catch(() => null));
      return { cart, mutated: true };
    }

    for (let attempt = 0; attempt < 3; attempt += 1) {
      // A read failure can't be allowed to throw: that would abort the whole reconcile and skip
      // the re-render, with the replacement line already in the cart. Treat it as this attempt
      // failing and try again.
      const current = await getCart().catch(() => null);
      if (!current || !Array.isArray(current.items)) continue;

      const stale = current.items.find(
        (item) =>
          item.properties?._bundle_id === bundleId &&
          !item.properties?._parent_sku &&
          item.variant_id === machine.variant_id
      );
      if (!stale) return { cart: current, mutated: true }; // already gone

      const res = await changeLine(stale.key, 0);
      if (res.ok) {
        // The removal landed. If the body won't parse, re-read rather than giving up the cart —
        // a null cart just costs the next pass a round-trip, it doesn't lose the mutation.
        const cart = (await res.json().catch(() => null)) || (await getCart().catch(() => null));
        return { cart, mutated: true };
      }
    }

    // Removals hit no validation, so reaching here means a repeated network fault. The cart
    // is left holding a visible duplicate rather than a missing machine — recoverable by the
    // shopper, and loud here so it's diagnosable.
    console.error(
      `Protection plan cart sync: rebuilt plan-less machine ${machine.variant_id} but could not remove its stale line — the cart now holds a duplicate`
    );
    // Hand back current state so the shopper sees the duplicate they can remove rather than a
    // stale view. Even if this read fails too, mutated stays true so the re-render still happens.
    return { cart: await getCart().catch(() => null), mutated: true };
  };

  // Resolves true when it changed the cart. `build: false` leaves the re-render to the caller —
  // the removal, which waits for this so the cart renders once, at where it settles.
  const reconcileOnce = async (event, { build = true } = {}) => {
    let changed = false;
    try {
      // Seed with the cart the theme's CartForm puts on the event (fresh from its change.js
      // response). Each change.js below returns the updated cart with reshuffled keys, which we
      // reuse as the next pass's cart — so no /cart.js round-trip unless we have nothing usable.
      let cart = event?.detail?.cart;

      // The pass cap is a safety net against an unexpected non-converging cart, not a
      // limit on real carts (one pass per out-of-sync plan line, plus one per stale machine).
      for (let pass = 0; pass < 20; pass += 1) {
        if (!cart || !Array.isArray(cart.items)) cart = await getCart();

        // Quantities first. A plan pending removal here would otherwise make its machine look
        // plan-less for one pass and get it rebuilt while the plan still exists.
        const fix = nextFix(cart);
        if (fix) {
          const res = await changeLine(fix.key, fix.quantity);
          if (!res.ok) {
            console.error(
              `Protection plan cart sync: change.js failed (${res.status}) for line ${fix.key}`
            );
            break;
          }
          changed = true;
          // change.js returns the full updated cart — use it next pass; refetch if unparseable.
          cart = await res.json().catch(() => null);
          continue;
        }

        const stale = nextStaleMachine(cart, rebuilt);
        if (!stale) break;

        // One rebuild attempt per bundle per page load. A rebuild that adds the replacement
        // but fails to remove the original leaves a duplicate; retrying it would add another,
        // so the cart can gain at most one per bundle even if the removal keeps failing.
        // Marking it BEFORE the attempt also keeps a stuck bundle from hiding the others —
        // nextStaleMachine skips what's in here, so the loop moves on instead of stalling.
        rebuilt.add(stale.properties._bundle_id);

        // mutated tells us whether the cart actually changed, separately from whether we came
        // away with a usable copy of it — a refused add changes nothing, while a failed cleanup
        // still leaves the replacement line in the cart and so still needs a re-render.
        // A null cart just costs the next pass a fresh read. Failures are logged in there.
        const { cart: rebuiltCart, mutated } = await rebuildStaleMachine(stale);
        if (mutated) changed = true;
        cart = rebuiltCart;
      }
    } catch (error) {
      console.error('Protection plan cart sync failed:', error);
    }

    // Re-render both surfaces, also when a later read threw after the cart had changed. Asked for
    // through the shared coordinator rather than by dispatching cart:build here: this reconcile
    // normally runs off a removal or a quantity change that has already started a build, and the
    // two repainting the same DOM from separate fetches is last-response-wins. Awaited, so the run
    // (and settled(), which the plan offer waits on before an add) ends once the new lines are in:
    // landing mid-add, they would replace the lines the offer's lock had frozen.
    if (changed && build) await requestBuild();
    return changed;
  };

  // One reconcile at a time, and none skipped. A call that lands while one is running waits for
  // it, then reconciles again from a fresh read: the running one works from a cart that can
  // predate this call's change (a removal's, say), so skipping would leave that change
  // unreconciled while the caller renders as if it had been. reconcileOnce never rejects.
  // Waiters wake in the order they queued, and the first to wake claims inFlight before the next
  // checks it, so a burst still runs one at a time (tests/theme/cart-reconcile-queue.test.js).
  const sync = async (event, options) => {
    let seed = event;
    waiting += 1;
    while (inFlight) {
      await inFlight;
      seed = null; // the cart it carried is older than the one the finished run left
    }
    waiting -= 1;
    inFlight = reconcileOnce(seed, options);
    try {
      return await inFlight;
    } finally {
      inFlight = null;
    }
  };

  document.addEventListener('cart:updated', sync);

  // Callable directly too, on the shared namespace, for a caller that needs the reconcile done
  // before it re-renders. The cart it's handed is used as the first pass's, as with the event.
  window.bfxCart.reconcile = (cart) => sync({ detail: { cart } }, { build: false });

  // Resolves once no reconcile is running or queued, for a caller about to change the cart itself
  // (the plan offer's add), which must not pick a line a running reconcile is about to rebuild.
  // Queued calls count too: between one run finishing and the next claiming inFlight, inFlight is
  // briefly null, and returning then would start the caller alongside that run.
  window.bfxCart.settled = async () => {
    while (inFlight || waiting > 0) await inFlight;
  };

  // bfx-cart-remove.js normally removes a machine and its plan together and fires
  // cart:updated, but when a failed change.js falls back to the anchor, removal is a plain
  // navigation that fires no event — the orphaned plan and any drifted quantity would sit
  // there until something else changed the cart. Reconcile once on load to catch that.
  //
  // With JS off this file doesn't run at all, so nothing reconciles during that session: the
  // anchor removes only the clicked line and a bundled machine leaves its plan behind. That's
  // cleaned up on the next load with JS enabled, not before.
  // Gated on the DOM actually containing a bundle so a normal cart never pays for the
  // /cart.js round-trip. The cart page's lines are in its server render, but everywhere else the
  // drawer's [data-products] starts empty and the theme fills it with a fetch after load, so an
  // empty root is watched until its first fill (or a few seconds) before the check. The plan
  // offer relies on this: after a failed orphan cleanup it reloads so this run can retry it.
  const LOAD_FILL_TIMEOUT = 5000;
  // An orphaned plan (its machine gone) renders as a plan line outside any bundle (cart-items.liquid).
  const ORPHAN_PLAN = ".cart__item[data-is-plan='true']:not(.cart__item--bundled-child)";

  // This run is the one that clears a plan a failed cleanup left orphaned, and the page renders
  // checkout live beside it. So while it runs, every surface showing one is held under the shared
  // cart lock, checkout included. Released when the run ends, plan removed or not: a cleanup that
  // fails again leaves it as a visible, removable line, and holding checkout for the rest of the
  // page would leave the shopper no way through.
  //
  // The cart page renders its empty state and its whole summary form server-side, gated on
  // cart.item_count (main-cart.liquid), and a re-render swaps only the lines. So a run that empties
  // it (its last line was an orphaned plan) reloads for the real empty state instead, as a removal
  // does, keeping the lock until the page goes. That reload finds no bundle, so it can't repeat.
  const syncOnLoadRun = async () => {
    const releases = [...document.querySelectorAll('[data-products]')]
      .filter((productsRoot) => productsRoot.querySelector?.(ORPHAN_PLAN))
      .map((productsRoot) => window.bfxCart.lock(productsRoot));
    let reloading = false;
    try {
      // The re-render stays inside the run, so settled() still covers it (the plan offer relies on
      // that); the empty check follows it.
      if (!(await sync()) || !document.body?.classList.contains('template-cart')) return;
      const cart = await getCart().catch((error) => {
        console.warn('Protection plan cart sync: re-read after load reconcile failed:', error);
        return null;
      });
      if (cart?.item_count === 0) {
        reloading = true;
        window.location.reload();
      }
    } finally {
      if (!reloading) releases.forEach((release) => release());
    }
  };
  const syncOnLoad = () => {
    if (document.querySelector('[data-bundle-id]')) {
      syncOnLoadRun();
      return;
    }
    const empty = [...document.querySelectorAll('[data-products]')].filter(
      (productsRoot) => !productsRoot.children.length
    );
    if (!empty.length) return;

    let timer = null;
    const observer = new MutationObserver(() => {
      clearTimeout(timer);
      observer.disconnect();
      if (document.querySelector('[data-bundle-id]')) syncOnLoadRun();
    });
    empty.forEach((productsRoot) => observer.observe(productsRoot, { childList: true }));
    // The cart page's drawer is rendered but never filled, so this is what ends that watch.
    timer = setTimeout(() => observer.disconnect(), LOAD_FILL_TIMEOUT);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncOnLoad, { once: true });
  } else {
    syncOnLoad();
  }
}
